import { type ReactNode, useEffect, useState } from 'react';
import { useGetMe } from '@/api/me/me';
import { SignIn } from '@/routes/sign-in';
import { initLiff, type LiffState } from '@/lib/liff';

/**
 * Establishes who the visitor is before any page renders.
 *
 * LIFF first: inside the LINE app that resolves silently, so a signed-in
 * person never sees a login screen. Only when LIFF is unconfigured (local
 * development) or its exchange failed does the email/password form appear —
 * hence the `/api/me` probe afterwards, which is the real test of whether a
 * session exists regardless of which door was used.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [liffState, setLiffState] = useState<LiffState | null>(null);

  useEffect(() => {
    let cancelled = false;
    void initLiff().then((state) => {
      if (!cancelled) setLiffState(state);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const session = useGetMe({
    query: {
      // Nothing to probe until LIFF has had its chance to set the cookie.
      enabled: liffState !== null && liffState.kind !== 'redirecting',
      retry: false,
    },
  });

  if (liffState === null || liffState.kind === 'redirecting') {
    return <Splash label="กำลังเชื่อมต่อ LINE…" />;
  }

  if (session.isPending) return <Splash label="กำลังโหลด…" />;

  if (session.isError) {
    return <SignIn liffError={liffState.kind === 'error' ? liffState.message : undefined} />;
  }

  return <>{children}</>;
}

function Splash({ label }: { label: string }) {
  return (
    <div className="grid min-h-full place-items-center p-8 text-sm text-ink-muted">{label}</div>
  );
}
